import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDate } from './dateUtils';
import { expandirReferencia } from './bibliaHelper';

/**
 * Utilidades para exportar eventos del calendario a PDF y CSV
 * 
 * Prioridad de eventos:
 * 1. Citas Ordenadas (DEBEN aparecer siempre)
 * 2. Lecturas (DEBEN aparecer siempre)
 * 3. Eventos (Deseables, se incluyen si hay espacio)
 */

/**
 * Prepara los datos de eventos con la priorización correcta
 * @param {Object} items - Objeto con arrays de citas, lecturas y eventos
 * @param {Date} date - Fecha para la cual se están preparando los datos
 * @returns {Array} Array de eventos formateados y priorizados
 */
function prepareEventData(items, date) {
  const data = [];
  
  // 1. PRIORIDAD ALTA: Citas Ordenadas (SIEMPRE incluidas)
  if (items.citas && items.citas.length > 0) {
    items.citas.forEach(cita => {
      data.push({
        tipo: 'Cita Ordenada',
        nombre: cita.nombre || 'Sin nombre',
        detalle: cita.descripcion || '',
        color: cita.color || '#6366f1',
        prioridad: 1
      });
    });
  }
  
  // 2. PRIORIDAD ALTA: Lecturas (SIEMPRE incluidas)
  if (items.lecturas && items.lecturas.length > 0) {
    items.lecturas.forEach(lectura => {
      const referencia = lectura.referencia ? expandirReferencia(lectura.referencia) : '';
      data.push({
        tipo: `Lectura: ${lectura.tipo || 'General'}`,
        nombre: lectura.subtipo || lectura.tipo || 'Lectura',
        detalle: referencia,
        color: lectura.color || '#10b981',
        prioridad: 2
      });
    });
  }
  
  // 3. PRIORIDAD MEDIA: Eventos (Deseables)
  if (items.eventos && items.eventos.length > 0) {
    items.eventos.forEach(evento => {
      data.push({
        tipo: 'Evento',
        nombre: evento.nombre || 'Sin nombre',
        detalle: evento.descripcion || '',
        color: evento.color || '#f59e0b',
        prioridad: 3
      });
    });
  }
  
  // Ordenar por prioridad
  return data.sort((a, b) => a.prioridad - b.prioridad);
}

/**
 * Exporta eventos a PDF
 * @param {Array} daysData - Array de objetos {date, items}
 * @param {string} viewType - Tipo de vista: 'day', 'week', 'month'
 * @param {string} title - Título del reporte
 */
export function exportToPDF(daysData, viewType, title) {
  const doc = new jsPDF({
    orientation: viewType === 'day' ? 'portrait' : 'landscape',
    unit: 'mm',
    format: 'a4'
  });
  
  // Configuración de colores
  const primaryColor = [99, 102, 241]; // Azul
  const textColor = [50, 50, 50]; // Gris oscuro
  
  // Título principal
  doc.setFontSize(18);
  doc.setTextColor(...primaryColor);
  doc.text(title, 14, 15);
  
  // Fecha de generación
  doc.setFontSize(10);
  doc.setTextColor(...textColor);
  doc.text(`Generado: ${formatDate(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 22);
  
  let startY = 30;
  
  // Contador total de eventos
  const totalCitas = daysData.reduce((sum, day) => sum + (day.items.citas?.length || 0), 0);
  const totalLecturas = daysData.reduce((sum, day) => sum + (day.items.lecturas?.length || 0), 0);
  const totalEventos = daysData.reduce((sum, day) => sum + (day.items.eventos?.length || 0), 0);
  const totalItems = totalCitas + totalLecturas + totalEventos;
  
  doc.setFontSize(11);
  doc.setTextColor(...textColor);
  doc.text(`Total de eventos: ${totalItems} (Citas: ${totalCitas}, Lecturas: ${totalLecturas}, Eventos: ${totalEventos})`, 14, startY);
  startY += 8;
  
  // Para vista día: mostrar TODOS los eventos
  // Para vista semana/mes: limitar para que quepa en una página
  const maxItemsPerDay = viewType === 'day' ? 1000 : (viewType === 'week' ? 15 : 5);
  
  daysData.forEach((dayData, index) => {
    const { date, items } = dayData;
    const eventData = prepareEventData(items, date);
    
    if (eventData.length === 0) return; // Saltar días sin eventos
    
    // Verificar si necesitamos una nueva página
    if (startY > 200) {
      doc.addPage();
      startY = 20;
    }
    
    // Encabezado del día
    doc.setFontSize(12);
    doc.setTextColor(...primaryColor);
    doc.text(formatDate(date, 'EEEE, d \'de\' MMMM \'de\' yyyy'), 14, startY);
    startY += 2;
    
    // Separar eventos por prioridad
    const citasData = eventData.filter(e => e.prioridad === 1);
    const lecturasData = eventData.filter(e => e.prioridad === 2);
    const eventosData = eventData.filter(e => e.prioridad === 3);
    
    // SIEMPRE incluir Citas y Lecturas
    let itemsToShow = [...citasData, ...lecturasData];
    
    // Agregar eventos si hay espacio
    const remainingSpace = maxItemsPerDay - itemsToShow.length;
    if (remainingSpace > 0 && eventosData.length > 0) {
      itemsToShow = [...itemsToShow, ...eventosData.slice(0, remainingSpace)];
    }
    
    // Crear tabla
    const tableData = itemsToShow.map(event => [
      event.tipo,
      event.nombre,
      event.detalle || '-'
    ]);
    
    autoTable(doc, {
      startY: startY,
      head: [['Tipo', 'Nombre', 'Detalle']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: textColor
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 45 }, // Tipo
        1: { cellWidth: 60 }, // Nombre
        2: { cellWidth: 'auto' } // Detalle
      },
      margin: { left: 14, right: 14 },
      didDrawPage: (data) => {
        // Pie de página
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Página ${data.pageNumber} de ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }
    });
    
    startY = doc.lastAutoTable.finalY + 8;
    
    // Mostrar advertencia si hay eventos que no se incluyeron
    const totalEvents = eventData.length;
    const shownEvents = itemsToShow.length;
    if (totalEvents > shownEvents) {
      doc.setFontSize(8);
      doc.setTextColor(200, 100, 100);
      doc.text(`⚠ ${totalEvents - shownEvents} evento(s) adicional(es) no mostrado(s) por espacio`, 14, startY);
      startY += 5;
    }
  });
  
  // Guardar PDF
  const fileName = `calendario-${viewType}-${formatDate(new Date(), 'yyyy-MM-dd-HHmm')}.pdf`;
  doc.save(fileName);
}

/**
 * Exporta eventos a CSV
 * @param {Array} daysData - Array de objetos {date, items}
 * @param {string} viewType - Tipo de vista: 'day', 'week', 'month'
 * @param {string} title - Título del reporte
 */
export function exportToCSV(daysData, viewType, title) {
  // Encabezados CSV
  const headers = ['Fecha', 'Tipo', 'Nombre', 'Detalle'];
  const rows = [headers];
  
  // Contador total de eventos
  const totalCitas = daysData.reduce((sum, day) => sum + (day.items.citas?.length || 0), 0);
  const totalLecturas = daysData.reduce((sum, day) => sum + (day.items.lecturas?.length || 0), 0);
  const totalEventos = daysData.reduce((sum, day) => sum + (day.items.eventos?.length || 0), 0);
  const totalItems = totalCitas + totalLecturas + totalEventos;
  
  // Agregar resumen
  rows.push([]);
  rows.push([`Reporte: ${title}`]);
  rows.push([`Generado: ${formatDate(new Date(), 'dd/MM/yyyy HH:mm')}`]);
  rows.push([`Total eventos: ${totalItems} (Citas: ${totalCitas}, Lecturas: ${totalLecturas}, Eventos: ${totalEventos})`]);
  rows.push([]);
  rows.push(headers);
  
  // Procesar cada día
  daysData.forEach(({ date, items }) => {
    const eventData = prepareEventData(items, date);
    
    if (eventData.length === 0) return;
    
    const dateStr = formatDate(date, 'dd/MM/yyyy (EEEE)');
    
    // Para vista día: incluir TODOS los eventos
    // Para otras vistas: priorizar Citas y Lecturas
    const maxItemsPerDay = viewType === 'day' ? 1000 : 20;
    
    // Separar por prioridad
    const citasData = eventData.filter(e => e.prioridad === 1);
    const lecturasData = eventData.filter(e => e.prioridad === 2);
    const eventosData = eventData.filter(e => e.prioridad === 3);
    
    // SIEMPRE incluir Citas y Lecturas
    let itemsToShow = [...citasData, ...lecturasData];
    
    // Agregar eventos si hay espacio
    const remainingSpace = maxItemsPerDay - itemsToShow.length;
    if (remainingSpace > 0 && eventosData.length > 0) {
      itemsToShow = [...itemsToShow, ...eventosData.slice(0, remainingSpace)];
    }
    
    itemsToShow.forEach(event => {
      rows.push([
        dateStr,
        event.tipo,
        event.nombre,
        event.detalle || '-'
      ]);
    });
    
    // Advertencia si hay eventos no incluidos
    if (eventData.length > itemsToShow.length) {
      rows.push([
        dateStr,
        '⚠ ADVERTENCIA',
        `${eventData.length - itemsToShow.length} evento(s) adicional(es) no mostrado(s)`,
        'Consultar vista diaria para ver todos los eventos'
      ]);
    }
    
    // Línea separadora entre días
    rows.push([]);
  });
  
  // Convertir a CSV
  const csvContent = rows.map(row => 
    row.map(cell => {
      // Escapar comillas y agregar comillas si contiene comas o saltos de línea
      const cellStr = String(cell || '');
      if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
        return `"${cellStr.replace(/"/g, '""')}"`;
      }
      return cellStr;
    }).join(',')
  ).join('\n');
  
  // Crear y descargar archivo
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `calendario-${viewType}-${formatDate(new Date(), 'yyyy-MM-dd-HHmm')}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Muestra un diálogo para elegir el formato de exportación
 * @param {Array} daysData - Array de objetos {date, items}
 * @param {string} viewType - Tipo de vista: 'day', 'week', 'month'
 * @param {string} title - Título del reporte
 * @returns {Promise} Promesa que se resuelve con el formato elegido
 */
export function showExportDialog(daysData, viewType, title) {
  return new Promise((resolve) => {
    const dialog = document.createElement('div');
    dialog.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    dialog.innerHTML = `
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 class="text-xl font-bold mb-4 text-gray-800">Exportar Calendario</h3>
        <p class="text-sm text-gray-600 mb-6">
          Selecciona el formato de exportación. 
          ${viewType !== 'day' ? '<br><strong>Nota:</strong> Las Citas y Lecturas siempre se incluirán. Los Eventos se incluirán según el espacio disponible.' : ''}
        </p>
        <div class="space-y-3">
          <button id="export-pdf" class="w-full py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
            </svg>
            Exportar como PDF
          </button>
          <button id="export-csv" class="w-full py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
            Exportar como CSV
          </button>
          <button id="export-cancel" class="w-full py-3 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors">
            Cancelar
          </button>
        </div>
      </div>
    `;
    
    document.body.appendChild(dialog);
    
    document.getElementById('export-pdf').onclick = () => {
      document.body.removeChild(dialog);
      exportToPDF(daysData, viewType, title);
      resolve('pdf');
    };
    
    document.getElementById('export-csv').onclick = () => {
      document.body.removeChild(dialog);
      exportToCSV(daysData, viewType, title);
      resolve('csv');
    };
    
    document.getElementById('export-cancel').onclick = () => {
      document.body.removeChild(dialog);
      resolve(null);
    };
    
    // Cerrar al hacer clic fuera del diálogo
    dialog.onclick = (e) => {
      if (e.target === dialog) {
        document.body.removeChild(dialog);
        resolve(null);
      }
    };
  });
}

export default {
  exportToPDF,
  exportToCSV,
  showExportDialog
};












